/*
 * bundle-selector.js
 *
 * Drives the NO CRASH bundle product page. The section renders the load state
 * (subscribe, 2 bags) server side, so this only ever moves the page from one
 * correct state to another.
 *
 * State: { bags, purchase: 'once' | 'sub' }
 *
 * Flavour is chosen per bag with the selects inside the selected card. They sit
 * outside the add to cart form and join it through their form attribute, as
 * line item properties. The variant posted is the one matching bag 1's flavour
 * at the chosen bag count. Each bag count has its own selling plan, carried on
 * the variant data; nothing here falls back to another tier's plan.
 */

if (!customElements.get('bundle-selector')) {
  customElements.define(
    'bundle-selector',
    class BundleSelector extends HTMLElement {
      connectedCallback() {
        const dataEl = this.querySelector('[data-ncb-data]');
        if (!dataEl) return;

        try {
          this.data = JSON.parse(dataEl.textContent);
        } catch (error) {
          console.error('bundle-selector: could not parse section data', error);
          return;
        }

        this.sectionId = this.dataset.sectionId;
        this.cards = Array.from(this.querySelectorAll('[data-ncb-card]'));
        this.tierInputs = Array.from(this.querySelectorAll('[data-ncb-tier]'));
        this.purchaseInputs = Array.from(this.querySelectorAll('[data-ncb-purchase]'));
        this.purchaseLabels = Array.from(this.querySelectorAll('[data-ncb-ptype]'));
        this.variantInput = this.querySelector('[data-ncb-variant-input]');
        this.planInput = this.querySelector('[data-ncb-plan-input]');
        this.submitButton = this.querySelector('[data-ncb-submit]');
        this.submitText = this.querySelector('[data-ncb-submit-text]');
        this.afterAtc = this.querySelector('[data-ncb-after-atc]');
        this.stepUpLine = this.querySelector('[data-ncb-stepup]');

        const section = this.closest('.ncb');
        this.section = section;
        this.sticky = section ? section.querySelector('[data-ncb-sticky]') : null;
        this.stickyButton = this.sticky ? this.sticky.querySelector('[data-ncb-sticky-button]') : null;
        this.stickyText = this.sticky ? this.sticky.querySelector('[data-ncb-sticky-text]') : null;

        const checkedTier = this.tierInputs.find((input) => input.checked);
        const checkedPurchase = this.purchaseInputs.find((input) => input.checked);

        this.state = {
          bags: checkedTier ? checkedTier.value : this.data.defaultBags,
          purchase: checkedPurchase ? checkedPurchase.value : this.data.defaultPurchase,
        };

        this.form = this.querySelector('form[action*="/cart/add"]');

        this.bindEvents();
        this.reclaimForm();
        this.render({ silent: true });
        this.setUpSealGuard();
        this.setUpStickyCta();
      }

      /* ------------------------------------------------------- form guard */

      /*
       * The Seal Subscriptions app embed targets form[action*=/cart/add] as a
       * fallback. It injects its own purchase-options widget and, critically,
       * deletes our selling_plan input and replaces it with its own enabled
       * one, which would put a subscription on every add to cart including
       * one-time purchases. The purchase toggle is the single source of truth,
       * so we take the form back whenever Seal touches it.
       */
      reclaimForm() {
        if (!this.form) return false;
        let changed = false;

        this.form.querySelectorAll('.sealsubs-target-element').forEach((node) => {
          node.remove();
          changed = true;
        });

        this.form.querySelectorAll('input[name="selling_plan"]').forEach((node) => {
          if (node !== this.planInput) {
            node.remove();
            changed = true;
          }
        });

        if (!this.planInput || !this.form.contains(this.planInput)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          input.setAttribute('data-ncb-plan-input', '');
          input.disabled = true;
          this.form.appendChild(input);
          this.planInput = input;
          changed = true;
        }

        return changed;
      }

      setUpSealGuard() {
        if (!this.form || !('MutationObserver' in window)) return;

        const observer = new MutationObserver(() => {
          if (this.reclaiming) return;
          this.reclaiming = true;
          const changed = this.reclaimForm();
          this.reclaiming = false;
          if (changed) this.renderForm(this.currentVariant());
        });

        observer.observe(this.form, { childList: true, subtree: true });
      }

      /* ------------------------------------------------------------ data */

      selectedCard() {
        return this.cards.find((card) => card.dataset.bags === this.state.bags);
      }

      firstFlavour() {
        const card = this.selectedCard();
        const select = card ? card.querySelector('[data-ncb-flavour][data-bag="1"]') : null;
        return select ? select.value : null;
      }

      flavours() {
        const card = this.selectedCard();
        if (!card) return [];
        return Array.from(card.querySelectorAll('[data-ncb-flavour]')).map((select) => select.value);
      }

      currentVariant() {
        const flavour = this.firstFlavour();
        return this.data.variants.find(
          (variant) => variant.bags === this.state.bags && (!flavour || variant.flavour === flavour)
        );
      }

      /* ---------------------------------------------------------- events */

      bindEvents() {
        this.purchaseInputs.forEach((input) => {
          input.addEventListener('change', () => {
            if (!input.checked || input.value === this.state.purchase) return;
            this.state.purchase = input.value;
            this.render();
          });
        });

        this.tierInputs.forEach((input) => {
          input.addEventListener('change', () => {
            if (!input.checked || input.value === this.state.bags) return;
            this.state.bags = input.value;
            this.render();
          });
        });

        this.querySelectorAll('[data-ncb-flavour]').forEach((select) => {
          select.addEventListener('change', () => this.render());
        });

        if (this.stickyButton && this.submitButton) {
          this.stickyButton.addEventListener('click', () => {
            this.submitButton.click();
          });
        }
      }

      /* ---------------------------------------------------------- render */

      render(options) {
        const opts = options || {};
        const current = this.currentVariant();

        this.dataset.purchase = this.state.purchase;
        this.renderPurchase();
        this.renderCards();
        this.renderForm(current);
        this.renderUrl(current);
        if (!opts.silent) this.renderGallery(current);

        if (!opts.silent) this.pushDataLayer();
      }

      renderPurchase() {
        this.purchaseLabels.forEach((label) => {
          label.classList.toggle('is-selected', label.dataset.ncbPtype === this.state.purchase);
        });
        this.purchaseInputs.forEach((input) => {
          const matches = input.value === this.state.purchase;
          if (input.checked !== matches) input.checked = matches;
        });
      }

      renderCards() {
        this.cards.forEach((card) => {
          const isSelected = card.dataset.bags === this.state.bags;
          card.classList.toggle('is-selected', isSelected);

          const input = card.querySelector('[data-ncb-tier]');
          if (input && input.checked !== isSelected) input.checked = isSelected;

          const extras = card.querySelector('[data-ncb-extras]');
          if (extras) extras.hidden = !isSelected;

          // Only the selected card's flavours may reach /cart/add; a disabled
          // control is left out of the form data.
          card.querySelectorAll('[data-ncb-flavour]').forEach((select) => {
            select.disabled = !isSelected;
          });

          const soldOut = card.querySelector('[data-ncb-soldout]');
          if (soldOut && isSelected) {
            const variant = this.currentVariant();
            soldOut.hidden = !!variant && variant.available;
          }
        });
      }

      renderForm(variant) {
        const available = !!variant && variant.available;
        const wantsSub = this.state.purchase === 'sub';
        const canSub = !!variant && !!variant.planId;

        if (this.variantInput && variant) this.variantInput.value = variant.id;

        if (this.planInput) {
          if (wantsSub && canSub) {
            this.planInput.value = variant.planId;
            this.planInput.disabled = false;
          } else {
            // One-time purchase: the selling plan must not reach /cart/add at all.
            this.planInput.value = '';
            this.planInput.disabled = true;
          }
        }

        let label = 'Sold out';
        let buyable = available;
        if (available && wantsSub && !canSub) {
          label = 'Subscription unavailable';
          buyable = false;
        } else if (available) {
          label = 'Add to basket · ' + (wantsSub ? variant.subPriceFmt : variant.priceFmt);
        }

        if (this.submitText) this.submitText.textContent = label;
        if (this.stickyText) this.stickyText.textContent = label;

        // Lines under the button: the skip and cancel line on any
        // subscription, the first month step-up only on 1 bag subscribe.
        const isSub = wantsSub && canSub;
        if (this.afterAtc) this.afterAtc.hidden = !isSub;
        if (this.stepUpLine) this.stepUpLine.hidden = !(isSub && variant.bags === '1');

        if (this.submitButton) {
          this.submitButton.disabled = !buyable;
          this.submitButton.classList.toggle('disabled', !buyable);
        }
        if (this.stickyButton) this.stickyButton.disabled = !buyable;
      }

      renderUrl(variant) {
        // Off the product page there is no variant in the address bar to keep
        // in step, so leave it alone.
        if (!this.hasAttribute('data-sync-url')) return;
        if (!variant || !window.history || !window.history.replaceState) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      /*
       * Selecting a tier or a flavour puts that pack shot in the main image
       * slot. With hide_variants on, only the current variant's pack shot is
       * listed at all; the others carry the --variant class, which only hides.
       */
      renderGallery(variant) {
        const gallery = document.querySelector('media-gallery#MediaGallery-' + this.sectionId);
        if (!gallery) return;

        const targetId = variant && variant.mediaId ? this.sectionId + '-' + variant.mediaId : null;

        // Which media belong to a variant has to come from the section data,
        // because the marker class is exactly what gets toggled below.
        if (!this.variantMediaIds) {
          this.variantMediaIds = new Set(
            this.data.variants.filter((v) => v.mediaId).map((v) => this.sectionId + '-' + v.mediaId)
          );
        }

        gallery.querySelectorAll('.product__media-item').forEach((item) => {
          const id = item.dataset.mediaId;
          if (!this.variantMediaIds.has(id)) return;
          item.classList.toggle('product__media-item--variant', id !== targetId);
        });

        gallery.querySelectorAll('.thumbnail-list__item').forEach((item) => {
          const id = item.dataset.target;
          if (!this.variantMediaIds.has(id)) return;
          item.classList.toggle('thumbnail-list_item--variant', id !== targetId);
        });

        if (
          targetId &&
          typeof gallery.setActiveMedia === 'function' &&
          gallery.querySelector('[data-media-id="' + targetId + '"]')
        ) {
          gallery.setActiveMedia(targetId, false);
        }
      }

      /* -------------------------------------------------------- tracking */

      pushDataLayer() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'bundle_select',
          bags: this.state.bags,
          purchase: this.state.purchase,
          flavours: this.flavours(),
        });
      }

      /* ---------------------------------------------------------- sticky */

      setUpStickyCta() {
        if (!this.sticky || !this.submitButton) return;

        let queued = false;
        const update = () => {
          queued = false;
          // Show it whenever the real button is off screen and the shopper has
          // reached the selector, so it is already there while they are working
          // through the bundle cards, not only after they scroll past the
          // button. Measured live rather than from an IntersectionObserver
          // entry, whose cached rect goes stale on a jump scroll.
          const viewport = window.innerHeight || document.documentElement.clientHeight;
          const button = this.submitButton.getBoundingClientRect();
          const selector = this.getBoundingClientRect();

          const buttonOnScreen = button.bottom > 0 && button.top < viewport;
          const reachedSelector = selector.top < viewport * 0.9;

          // Scoped to its section off the product page, so it does not trail
          // the shopper through the rest of a long homepage.
          let leftScope = false;
          if (this.dataset.stickyScope === 'section' && this.section) {
            const rect = this.section.getBoundingClientRect();
            leftScope = rect.bottom <= 0 || rect.top >= viewport;
          }

          this.sticky.hidden =
            buttonOnScreen || !reachedSelector || leftScope || this.cartDrawerOpen();
          this.liftFloatingWidgets();
        };
        const schedule = () => {
          if (queued) return;
          queued = true;
          window.requestAnimationFrame(update);
        };

        this.syncSticky = schedule;

        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule, { passive: true });
        this.watchCartDrawer();
        update();
      }

      /* --------------------------------------------------------- cart drawer */

      /*
       * Adding to cart opens the theme's cart drawer straight over the sticky
       * bar. The bar must get out of the way or it covers the drawer's own
       * Checkout button. The drawer marks itself open with .active (and the
       * body with .overflow-hidden), neither of which fires an event, so its
       * class attribute is observed directly.
       */
      cartDrawerOpen() {
        if (document.body.classList.contains('overflow-hidden')) return true;
        if (!this.cartDrawers) return false;
        return this.cartDrawers.some(
          (drawer) => drawer.classList.contains('active') || drawer.hasAttribute('open')
        );
      }

      watchCartDrawer() {
        this.cartDrawers = Array.from(document.querySelectorAll('cart-drawer, cart-notification'));
        if (!('MutationObserver' in window)) return;

        const observer = new MutationObserver(() => this.syncSticky());
        this.cartDrawers.forEach((drawer) =>
          observer.observe(drawer, { attributes: true, attributeFilter: ['class', 'open'] })
        );
        // The drawer also locks the body, which covers notification-style carts.
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      }

      /* ------------------------------------------------- floating launchers */

      /*
       * The Shopify Inbox chat pins itself to the bottom right of the
       * viewport, over the sticky bar. Lift it clear while the bar is showing.
       * Its host is a plain custom element, so bundle-selector.css does the
       * work and this only feeds it the height to clear.
       */
      liftFloatingWidgets() {
        const shown = !this.sticky.hidden;
        const lift = shown ? Math.round(this.sticky.getBoundingClientRect().height) + 12 : 0;
        const root = document.documentElement;

        root.style.setProperty('--ncb-widget-lift', lift + 'px');
        document.body.classList.toggle('ncb-widgets-lifted', shown);
      }
    }
  );
}

/*
 * ncb-review-strip
 *
 * A compact, auto-advancing strip of reviews under the add to cart button. It
 * holds no reviews of its own: it waits for the Klaviyo Reviews carousel on the
 * same page to render, then shows the first few of whatever that carousel
 * returned, in the app's order. Nothing is picked, hidden or featured here.
 * It sits outside the add to cart form, so reclaimForm() never sees it.
 */
if (!customElements.get('ncb-review-strip')) {
  customElements.define(
    'ncb-review-strip',
    class NcbReviewStrip extends HTMLElement {
      connectedCallback() {
        this.max = parseInt(this.dataset.max, 10) || 4;
        this.index = 0;
        if (this.build()) return;

        if (!('MutationObserver' in window)) return;
        this.observer = new MutationObserver(() => {
          if (this.build()) this.stopWatching();
        });
        this.observer.observe(document.body, { childList: true, subtree: true });
        // Give up quietly if the carousel never arrives.
        this.giveUp = window.setTimeout(() => this.stopWatching(), 20000);
      }

      disconnectedCallback() {
        this.stopWatching();
        if (this.timer) window.clearInterval(this.timer);
      }

      stopWatching() {
        if (this.observer) this.observer.disconnect();
        this.observer = null;
        if (this.giveUp) window.clearTimeout(this.giveUp);
      }

      readReviews() {
        const slides = Array.from(document.querySelectorAll('.kl_reviews__carousel .swiper-slide'));
        const reviews = [];
        for (const slide of slides) {
          if (reviews.length >= this.max) break;
          const body = slide.querySelector('.kl_reviews__carousel__content_body');
          const author = slide.querySelector('.kl_reviews__carousel__author > div');
          const rating = slide.querySelector('[aria-label$="star review"]');
          const text = body ? body.textContent.trim() : '';
          if (!text) continue;
          reviews.push({
            text,
            author: author ? author.textContent.trim() : '',
            stars: rating ? parseInt(rating.getAttribute('aria-label'), 10) || 5 : 5,
            verified: !!slide.querySelector('.kl_reviews__review__verified'),
          });
        }
        return reviews;
      }

      build() {
        if (this.built) return true;
        const reviews = this.readReviews();
        if (!reviews.length) return false;
        this.built = true;

        const list = document.createElement('div');
        list.className = 'ncb-review-strip__track';
        list.setAttribute('aria-live', 'off');

        reviews.forEach((review, i) => {
          const item = document.createElement('figure');
          item.className = 'ncb-review-strip__item' + (i === 0 ? ' is-active' : '');
          item.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');

          const stars = document.createElement('span');
          stars.className = 'ncb-review-strip__stars';
          stars.setAttribute('aria-label', review.stars + ' out of 5 stars');
          stars.textContent = '★'.repeat(review.stars) + '☆'.repeat(Math.max(0, 5 - review.stars));

          const quote = document.createElement('blockquote');
          quote.className = 'ncb-review-strip__text';
          quote.textContent = review.text;

          const who = document.createElement('figcaption');
          who.className = 'ncb-review-strip__author';
          who.textContent = review.author + (review.verified ? ', verified buyer' : '');

          item.append(stars, quote, who);
          list.appendChild(item);
        });

        this.replaceChildren(list);
        this.items = Array.from(list.children);
        this.hidden = false;

        if (this.items.length > 1) {
          this.timer = window.setInterval(() => {
            if (this.paused) return;
            this.show((this.index + 1) % this.items.length);
          }, 5000);
          this.addEventListener('mouseenter', () => (this.paused = true));
          this.addEventListener('mouseleave', () => (this.paused = false));
          this.addEventListener('focusin', () => (this.paused = true));
          this.addEventListener('focusout', () => (this.paused = false));
        }
        return true;
      }

      show(next) {
        this.items[this.index].classList.remove('is-active');
        this.items[this.index].setAttribute('aria-hidden', 'true');
        this.index = next;
        this.items[this.index].classList.add('is-active');
        this.items[this.index].setAttribute('aria-hidden', 'false');
      }
    }
  );
}